(() => {
  'use strict';

  const ROOT_SELECTOR = 'md-smart-cart';
  const STATE = window.__MDSmartCartState || (window.__MDSmartCartState = {
    open: false,
    lastTrigger: null,
    syncTimer: 0,
    fetchPatched: false,
    globalBound: false
  });

  const toBool = (value) => value === true || value === 'true';
  const sleepFrame = () => new Promise((resolve) => requestAnimationFrame(resolve));

  const activeCart = () => {
    return Array.from(document.querySelectorAll(ROOT_SELECTOR)).find(
      (element) => element.dataset.enabled === 'true' && !element.classList.contains('md-smart-cart--disabled')
    ) || null;
  };

  const normalizeRoot = (root) => {
    const value = root || window.Shopify?.routes?.root || '/';
    return value.endsWith('/') ? value : `${value}/`;
  };

  const requestUrl = (input) => {
    try {
      if (input instanceof Request) return new URL(input.url, window.location.origin);
      return new URL(String(input), window.location.origin);
    } catch (error) {
      return null;
    }
  };

  const isCartEndpoint = (url, endpoint) => {
    if (!url) return false;
    const path = url.pathname.replace(/\/+/g, '/').replace(/\/$/, '');
    return path.endsWith(`/cart/${endpoint}`) || path.endsWith(`/cart/${endpoint}.js`);
  };

  const markerHeaderPresent = (input, init) => {
    try {
      const headers = new Headers(input instanceof Request ? input.headers : init?.headers);
      return headers.get('X-MD-Smart-Cart') === '1';
    } catch (error) {
      return false;
    }
  };

  class MDSmartCart extends HTMLElement {
    constructor() {
      super();
      this._busy = false;
      this._refreshPromise = null;
      this._recommendationController = null;
      this._nativeObserver = null;
      this._nativeCloseQueued = false;
      this._boundClick = this._onClick.bind(this);
      this._boundKeydown = this._onKeydown.bind(this);
    }

    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.sectionId = this.dataset.sectionId;
      this.rootUrl = normalizeRoot(this.dataset.rootUrl);
      this.errorText = this.dataset.errorText || 'Something went wrong. Please try again.';
      this.loadingText = this.dataset.loadingText || 'Updating…';
      this.debug = toBool(this.dataset.debug);

      this.addEventListener('click', this._boundClick);
      this._prepareGoals();
      this._loadDynamicRecommendations();

      if (STATE.open) {
        this.open(STATE.lastTrigger, true);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._boundClick);
      document.removeEventListener('keydown', this._boundKeydown, true);
      this._recommendationController?.abort();
      this._nativeObserver?.disconnect();
    }

    log(...args) {
      if (this.debug) console.info('[MD Smart Cart]', ...args);
    }

    open(trigger = null, restoring = false) {
      if (this.dataset.enabled !== 'true') return;

      if (trigger instanceof HTMLElement) {
        STATE.lastTrigger = trigger;
      }

      STATE.open = true;
      this.setAttribute('aria-hidden', 'false');
      this.classList.add('is-open');
      document.body.classList.add('md-smart-cart-locked');

      if (toBool(this.dataset.suppressNative)) {
        this._startNativeDrawerGuard();
      }

      document.removeEventListener('keydown', this._boundKeydown, true);
      document.addEventListener('keydown', this._boundKeydown, true);

      if (!restoring) {
        requestAnimationFrame(() => {
          const closeButton = this.querySelector('[data-mdsc-close]:not(.md-smart-cart__overlay)');
          closeButton?.focus({ preventScroll: true });
        });
      }

      document.dispatchEvent(new CustomEvent('md:cart:opened', {
        detail: { sectionId: this.sectionId }
      }));
    }

    close({ restoreFocus = true } = {}) {
      if (!this.classList.contains('is-open') && !STATE.open) return;

      STATE.open = false;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('md-smart-cart-locked');
      document.removeEventListener('keydown', this._boundKeydown, true);
      this._nativeObserver?.disconnect();
      this._nativeObserver = null;

      if (restoreFocus && STATE.lastTrigger instanceof HTMLElement && document.contains(STATE.lastTrigger)) {
        STATE.lastTrigger.focus({ preventScroll: true });
      }

      document.dispatchEvent(new CustomEvent('md:cart:closed', {
        detail: { sectionId: this.sectionId }
      }));
    }

    async _onClick(event) {
      const closeControl = event.target.closest('[data-mdsc-close]');
      if (closeControl && this.contains(closeControl)) {
        event.preventDefault();
        event.stopPropagation();
        this.close();
        return;
      }

      const quantityButton = event.target.closest('[data-mdsc-quantity]');
      if (quantityButton && this.contains(quantityButton)) {
        event.preventDefault();
        await this._handleQuantity(quantityButton);
        return;
      }

      const removeButton = event.target.closest('[data-mdsc-remove]');
      if (removeButton && this.contains(removeButton)) {
        event.preventDefault();
        await this._changeLine(removeButton.dataset.key, 0, removeButton.closest('[data-mdsc-line]'));
        return;
      }

      const recommendationButton = event.target.closest('[data-mdsc-add-recommendation]');
      if (recommendationButton && this.contains(recommendationButton)) {
        event.preventDefault();
        await this._addRecommendation(recommendationButton);
        return;
      }

      const checkoutButton = event.target.closest('[data-mdsc-checkout]');
      if (checkoutButton && this.contains(checkoutButton)) {
        event.preventDefault();
        this._goToCheckout(checkoutButton.dataset.discountCode || '');
      }
    }

    _onKeydown(event) {
      if (!STATE.open || !this.classList.contains('is-open')) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = this._focusableElements();
      if (!focusable.length) {
        event.preventDefault();
        this.querySelector('.md-smart-cart__drawer')?.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    _focusableElements() {
      const selector = [
        'a[href]:not([tabindex="-1"])',
        'button:not([disabled]):not([tabindex="-1"])',
        'input:not([disabled]):not([tabindex="-1"])',
        'select:not([disabled]):not([tabindex="-1"])',
        'textarea:not([disabled]):not([tabindex="-1"])',
        '[tabindex]:not([tabindex="-1"])'
      ].join(',');

      return Array.from(this.querySelectorAll(selector)).filter((element) => {
        return !element.hidden && element.getClientRects().length > 0;
      });
    }

    _startNativeDrawerGuard() {
      this._closeNativeCartDrawer();
      this._nativeObserver?.disconnect();

      this._nativeObserver = new MutationObserver(() => {
        if (!STATE.open || this._nativeCloseQueued) return;
        this._nativeCloseQueued = true;
        requestAnimationFrame(() => {
          this._nativeCloseQueued = false;
          this._closeNativeCartDrawer();
        });
      });

      this._nativeObserver.observe(document.documentElement, {
        subtree: true,
        attributes: true,
        attributeFilter: ['open', 'class', 'aria-hidden']
      });
    }

    _closeNativeCartDrawer() {
      const selectors = [
        'theme-drawer#cart-drawer',
        '#cart-drawer:not(md-smart-cart)',
        '#CartDrawer',
        'cart-drawer',
        'cart-drawer-component',
        '[data-cart-drawer]'
      ];

      const visited = new Set();

      document.querySelectorAll(selectors.join(',')).forEach((candidate) => {
        if (this.contains(candidate)) return;

        const drawer = candidate.matches('theme-drawer')
          ? candidate
          : candidate.closest('theme-drawer') || candidate;

        if (visited.has(drawer)) return;
        visited.add(drawer);

        try {
          if (typeof drawer.close === 'function') {
            drawer.close();
          } else {
            const dialog = drawer.matches('dialog') ? drawer : drawer.querySelector?.('dialog[open]');
            if (dialog && typeof dialog.close === 'function') dialog.close();
            drawer.removeAttribute?.('open');
            drawer.classList?.remove('is-open', 'active', 'open');
            drawer.setAttribute?.('aria-hidden', 'true');
          }
        } catch (error) {
          this.log('Native drawer close fallback failed', error);
        }
      });
    }

    _prepareGoals() {
      const goalContainer = this.querySelector('[data-mdsc-goals]');
      const progress = this.querySelector('[data-mdsc-progress]');
      if (!goalContainer || !progress) return;

      const goals = Array.from(goalContainer.querySelectorAll('[data-mdsc-goal]'))
        .sort((a, b) => Number(a.dataset.quantity) - Number(b.dataset.quantity));

      goals.forEach((goal) => goalContainer.appendChild(goal));
      goalContainer.style.setProperty('--mdsc-goal-count', String(Math.max(goals.length, 1)));

      const count = Number(progress.dataset.count || this.dataset.itemCount || 0);
      const max = goals.length ? Number(goals[goals.length - 1].dataset.quantity) : 0;
      const meter = this.querySelector('[data-mdsc-progress-meter]');
      const fill = this.querySelector('[data-mdsc-progress-fill]');
      const heading = this.querySelector('[data-mdsc-progress-heading]');
      const checkout = this.querySelector('[data-mdsc-checkout]');

      if (meter) {
        meter.setAttribute('aria-valuemax', String(max));
        meter.setAttribute('aria-valuenow', String(count));
      }

      const percent = max > 0 ? Math.min(100, Math.max(0, (count / max) * 100)) : 0;
      if (fill) fill.style.width = `${percent}%`;

      let nextGoal = null;
      let achievedDiscount = '';

      goals.forEach((goal) => {
        const required = Number(goal.dataset.quantity);
        const complete = count >= required;
        goal.classList.toggle('is-complete', complete);
        goal.classList.remove('is-active');

        if (complete && goal.dataset.discountCode) {
          achievedDiscount = goal.dataset.discountCode;
        }

        if (!complete && !nextGoal) nextGoal = goal;

        const state = goal.querySelector('[data-mdsc-goal-state]');
        if (state) {
          state.textContent = complete
            ? (goal.dataset.achieved || 'Goal achieved')
            : `${Math.max(0, required - count)} item${required - count === 1 ? '' : 's'} remaining`;
        }
      });

      if (nextGoal) nextGoal.classList.add('is-active');

      if (heading) {
        if (!nextGoal) {
          heading.textContent = progress.dataset.completeText || 'Offer unlocked.';
        } else {
          const required = Number(nextGoal.dataset.quantity);
          const remaining = Math.max(0, required - count);
          const message = nextGoal.dataset.nextMessage || progress.dataset.headingTemplate || 'Add [remaining] More to [reward]';
          heading.textContent = this._replaceGoalTokens(message, {
            remaining,
            quantity: required,
            reward: nextGoal.dataset.reward || ''
          });
        }
      }

      if (checkout) checkout.dataset.discountCode = achievedDiscount;
    }

    _replaceGoalTokens(template, values) {
      return String(template)
        .replace(/\[remaining\]/gi, String(values.remaining))
        .replace(/\[quantity\]/gi, String(values.quantity))
        .replace(/\[reward\]/gi, String(values.reward));
    }

    async _handleQuantity(button) {
      if (this._busy || button.disabled) return;

      const current = Number(button.dataset.current || 0);
      const min = Number(button.dataset.min || 1);
      const increment = Math.max(1, Number(button.dataset.increment || 1));
      const max = button.dataset.max ? Number(button.dataset.max) : null;
      let quantity = current;

      if (button.dataset.mdscQuantity === 'plus') {
        quantity = current + increment;
        if (Number.isFinite(max)) quantity = Math.min(quantity, max);
      } else {
        quantity = current <= min ? 0 : Math.max(min, current - increment);
      }

      await this._changeLine(button.dataset.key, quantity, button.closest('[data-mdsc-line]'));
    }

    async _changeLine(key, quantity, lineElement) {
      if (this._busy || !key) return;
      this._busy = true;
      this._setLineLoading(lineElement, true);
      this._clearErrors();

      try {
        const data = await this._requestJson(`${this.rootUrl}cart/change.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'X-MD-Smart-Cart': '1'
          },
          body: JSON.stringify({
            id: key,
            quantity,
            sections: [this.sectionId],
            sections_url: window.location.pathname
          })
        });

        await this._applyCartResponse(data, true);
      } catch (error) {
        this._showLineError(lineElement, error.message || this.errorText);
        this._announce(error.message || this.errorText);
        this._setLineLoading(lineElement, false);
      } finally {
        this._busy = false;
      }
    }

    async _addRecommendation(button) {
      if (this._busy || button.disabled || button.classList.contains('is-loading')) return;

      const variantId = Number(button.dataset.variantId);
      if (!variantId) return;

      this._busy = true;
      button.classList.add('is-loading');
      button.disabled = true;
      const originalText = button.textContent;
      button.textContent = this.loadingText;
      this._clearErrors();

      try {
        const data = await this._requestJson(`${this.rootUrl}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'X-MD-Smart-Cart': '1'
          },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1 }],
            sections: [this.sectionId],
            sections_url: window.location.pathname
          })
        });

        await this._applyCartResponse(data, true);
      } catch (error) {
        this._showGlobalError(error.message || this.errorText);
        this._announce(error.message || this.errorText);
        button.classList.remove('is-loading');
        button.disabled = false;
        button.textContent = originalText;
      } finally {
        this._busy = false;
      }
    }

    async _requestJson(url, options) {
      const response = await window.fetch(url, options);
      let data = null;

      try {
        data = await response.json();
      } catch (error) {
        if (!response.ok) throw new Error(this.errorText);
      }

      if (!response.ok || data?.status >= 400) {
        throw new Error(data?.description || data?.message || this.errorText);
      }

      return data;
    }

    async _applyCartResponse(data, keepOpen) {
      const sections = data?.sections;
      const html = sections?.[this.sectionId] || (sections ? Object.values(sections).find(Boolean) : null);

      if (html) {
        this._replaceFromHtml(html, keepOpen);
        return;
      }

      await this.refresh(keepOpen);
    }

    _replaceFromHtml(html, keepOpen = STATE.open) {
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const replacement = documentFragment.querySelector(ROOT_SELECTOR);

      if (!replacement) {
        throw new Error('The smart-cart section could not be rendered.');
      }

      const count = Number(replacement.dataset.itemCount || 0);
      STATE.open = Boolean(keepOpen);
      this.replaceWith(replacement);
      MDSmartCart.updateCartBadges(count);

      queueMicrotask(() => {
        document.dispatchEvent(new CustomEvent('md:cart:updated', {
          detail: { itemCount: count, sectionId: replacement.dataset.sectionId }
        }));
      });
    }

    async refresh(keepOpen = STATE.open) {
      if (this._refreshPromise) return this._refreshPromise;

      this._refreshPromise = (async () => {
        const url = new URL(window.location.href);
        url.searchParams.set('sections', this.sectionId);

        const response = await window.fetch(url.toString(), {
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'X-MD-Smart-Cart': '1'
          }
        });

        if (!response.ok) throw new Error(this.errorText);
        const sections = await response.json();
        const html = sections?.[this.sectionId] || (sections ? Object.values(sections).find(Boolean) : null);

        if (!html) throw new Error('Shopify returned an empty smart-cart section.');
        this._replaceFromHtml(html, keepOpen);
      })().finally(() => {
        this._refreshPromise = null;
      });

      return this._refreshPromise;
    }

    scheduleSync(openAfter = false, delay = 90) {
      window.clearTimeout(STATE.syncTimer);
      STATE.syncTimer = window.setTimeout(() => {
        const current = activeCart();
        if (!current) return;
        current.refresh(openAfter || STATE.open).catch((error) => {
          current._showGlobalError(error.message || current.errorText);
          current.log('Scheduled refresh failed', error);
        });
      }, delay);
    }

    async verifyExternalAdd(previousCount) {
      try {
        const cart = await this._requestJson(`${this.rootUrl}cart.js`, {
          headers: {
            Accept: 'application/json',
            'X-MD-Smart-Cart': '1'
          }
        });

        if (Number(cart.item_count) > Number(previousCount)) {
          this.scheduleSync(true, 0);
        } else if (STATE.open) {
          this.scheduleSync(true, 0);
        }
      } catch (error) {
        this.log('External form verification failed', error);
      }
    }

    _setLineLoading(line, loading) {
      if (!line) return;
      line.classList.toggle('is-loading', loading);

      line.querySelectorAll('button').forEach((button) => {
        if (loading) {
          button.dataset.mdscWasDisabled = String(button.disabled);
          button.disabled = true;
        } else {
          button.disabled = button.dataset.mdscWasDisabled === 'true';
          delete button.dataset.mdscWasDisabled;
        }
      });

      line.setAttribute('aria-busy', String(loading));
    }

    _showLineError(line, message) {
      const target = line?.querySelector('[data-mdsc-line-error]');
      if (!target) {
        this._showGlobalError(message);
        return;
      }
      target.textContent = message;
      target.hidden = false;
    }

    _showGlobalError(message) {
      const target = this.querySelector('[data-mdsc-global-error]');
      if (!target) return;
      target.textContent = message;
      target.hidden = false;
    }

    _clearErrors() {
      this.querySelectorAll('[data-mdsc-line-error], [data-mdsc-global-error]').forEach((target) => {
        target.textContent = '';
        target.hidden = true;
      });
    }

    _announce(message) {
      const status = this.querySelector('[data-mdsc-status]');
      if (!status) return;
      status.textContent = '';
      requestAnimationFrame(() => {
        status.textContent = message;
      });
    }

    _goToCheckout(discountCode) {
      const checkoutPath = `${this.rootUrl}checkout`;

      if (discountCode) {
        const discountPath = `${this.rootUrl}discount/${encodeURIComponent(discountCode)}`;
        window.location.assign(`${discountPath}?redirect=${encodeURIComponent(checkoutPath)}`);
      } else {
        window.location.assign(checkoutPath);
      }
    }

    async _loadDynamicRecommendations() {
      const container = this.querySelector('[data-mdsc-dynamic-recommendations]');
      if (!container || !container.dataset.productId) return;

      this._recommendationController?.abort();
      this._recommendationController = new AbortController();

      const productId = container.dataset.productId;
      const limit = Math.min(10, Math.max(1, Number(container.dataset.limit || 4)));
      const url = `${this.rootUrl}recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=${limit}&intent=related`;

      try {
        const response = await window.fetch(url, {
          signal: this._recommendationController.signal,
          headers: {
            Accept: 'application/json',
            'X-MD-Smart-Cart': '1'
          }
        });

        if (!response.ok) throw new Error('Recommendations are unavailable.');
        const payload = await response.json();
        const cartIds = this._cartProductIds();
        const excludeCart = toBool(container.dataset.excludeCart);
        const excludeUnavailable = toBool(container.dataset.excludeUnavailable);

        const products = (payload.products || [])
          .filter((product, index, all) => all.findIndex((item) => item.id === product.id) === index)
          .filter((product) => !excludeCart || !cartIds.includes(Number(product.id)))
          .filter((product) => !excludeUnavailable || product.available)
          .slice(0, limit);

        container.replaceChildren();

        products.forEach((product) => {
          container.appendChild(this._buildRecommendationCard(product, container.dataset));
        });

        if (!products.length) {
          container.closest('[data-mdsc-recommendations]')?.remove();
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.log('Recommendation request failed', error);
        container.closest('[data-mdsc-recommendations]')?.remove();
      }
    }

    _cartProductIds() {
      const script = this.querySelector('[data-mdsc-cart-product-ids]');
      if (!script) return [];

      try {
        return JSON.parse(script.textContent || '[]').map(Number);
      } catch (error) {
        return [];
      }
    }

    _buildRecommendationCard(product, settings) {
      const card = document.createElement('article');
      card.className = 'md-smart-cart__recommendation-card';
      card.dataset.productId = String(product.id);

      const imageLink = document.createElement('a');
      imageLink.className = 'md-smart-cart__recommendation-image-link';
      imageLink.href = product.url || `${this.rootUrl}products/${product.handle}`;

      const price = Number(product.price || 0);
      const compareAt = Number(product.compare_at_price || product.compare_at_price_max || 0);
      const isSale = compareAt > price;

      if (toBool(settings.showBadge) && isSale) {
        const badge = document.createElement('span');
        badge.className = 'md-smart-cart__sale-badge';
        badge.textContent = 'Sale';
        imageLink.appendChild(badge);
      }

      const image = document.createElement('img');
      image.className = 'md-smart-cart__recommendation-image';
      image.loading = 'lazy';
      image.alt = product.title || '';
      image.src = this._productImageUrl(product);
      image.width = 260;
      image.height = 273;
      imageLink.appendChild(image);

      const copy = document.createElement('div');
      copy.className = 'md-smart-cart__recommendation-copy';

      if (toBool(settings.showVendor) && product.vendor) {
        const vendor = document.createElement('div');
        vendor.className = 'md-smart-cart__recommendation-vendor';
        vendor.textContent = product.vendor;
        copy.appendChild(vendor);
      }

      const title = document.createElement('a');
      title.className = 'md-smart-cart__recommendation-title';
      title.href = imageLink.href;
      title.textContent = product.title || '';
      copy.appendChild(title);

      const priceWrap = document.createElement('div');
      priceWrap.className = 'md-smart-cart__recommendation-price';

      const currentPrice = document.createElement('strong');
      currentPrice.textContent = this._formatMoney(price);
      priceWrap.appendChild(currentPrice);

      if (toBool(settings.showCompare) && isSale) {
        const compare = document.createElement('s');
        compare.textContent = this._formatMoney(compareAt);
        priceWrap.appendChild(compare);
      }

      copy.appendChild(priceWrap);

      const variants = Array.isArray(product.variants) ? product.variants : [];
      const availableVariants = variants.filter((variant) => variant.available);
      const singleVariant = variants.length === 1 ? variants[0] : null;

      if (!product.available || !availableVariants.length) {
        const soldOut = document.createElement('button');
        soldOut.type = 'button';
        soldOut.className = 'md-smart-cart__recommendation-button';
        soldOut.disabled = true;
        soldOut.textContent = settings.soldOutText || 'Sold Out';
        copy.appendChild(soldOut);
      } else if (singleVariant?.available) {
        const add = document.createElement('button');
        add.type = 'button';
        add.className = 'md-smart-cart__recommendation-button';
        add.dataset.mdscAddRecommendation = '';
        add.dataset.variantId = String(singleVariant.id);
        add.textContent = settings.addText || '+ Add';
        copy.appendChild(add);
      } else {
        const options = document.createElement('a');
        options.className = 'md-smart-cart__recommendation-button';
        options.href = imageLink.href;
        options.textContent = settings.optionsText || 'Choose Options';
        copy.appendChild(options);
      }

      card.append(imageLink, copy);
      return card;
    }

    _productImageUrl(product) {
      const featured = product.featured_image;
      if (typeof featured === 'string' && featured) return featured;
      if (featured?.url) return featured.url;
      if (featured?.src) return featured.src;

      const firstImage = Array.isArray(product.images) ? product.images[0] : null;
      if (typeof firstImage === 'string' && firstImage) return firstImage;
      if (firstImage?.url) return firstImage.url;
      if (firstImage?.src) return firstImage.src;

      return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(
        '<svg xmlns="http://www.w3.org/2000/svg" width="260" height="273" viewBox="0 0 260 273"><rect width="260" height="273" fill="#eef0f2"/><path d="M95 105h70v63H95z" fill="none" stroke="#aeb5bc" stroke-width="4"/><circle cx="118" cy="128" r="9" fill="#aeb5bc"/><path d="m101 160 22-22 16 16 10-10 10 16" fill="none" stroke="#aeb5bc" stroke-width="4"/></svg>'
      );
    }

    _formatMoney(cents) {
      const format = (this.dataset.moneyFormat || '${{amount}}').replace(/<[^>]*>/g, '');
      const value = Number(cents || 0);
      const tokenMatch = format.match(/\{\{\s*(\w+)\s*\}\}/);
      const token = tokenMatch?.[1] || 'amount';

      const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
        if (!Number.isFinite(number)) return '0';
        const fixed = (number / 100).toFixed(precision);
        const parts = fixed.split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return parts[1] ? `${parts[0]}${decimal}${parts[1]}` : parts[0];
      };

      let formatted;
      switch (token) {
        case 'amount_no_decimals':
          formatted = formatWithDelimiters(value, 0);
          break;
        case 'amount_with_comma_separator':
          formatted = formatWithDelimiters(value, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          formatted = formatWithDelimiters(value, 0, '.', ',');
          break;
        case 'amount_with_apostrophe_separator':
          formatted = formatWithDelimiters(value, 2, "'", '.');
          break;
        default:
          formatted = formatWithDelimiters(value, 2);
      }

      return format.replace(tokenMatch?.[0] || '{{amount}}', formatted);
    }

    static updateCartBadges(count) {
      const numericCount = Math.max(0, Number(count) || 0);
      const value = String(numericCount);
      const visibleValue = numericCount > 99 ? '99+' : value;

      document.querySelectorAll('[data-cart-count], .header-actions__cart-count, [data-testid="cart-count"]').forEach((element) => {
        if (!element.closest(ROOT_SELECTOR)) element.textContent = visibleValue;
      });

      document.querySelectorAll('.cart-count-bubble').forEach((bubble) => {
        if (bubble.closest(ROOT_SELECTOR)) return;
        bubble.hidden = numericCount === 0;
        bubble.setAttribute('aria-label', `${value} item${numericCount === 1 ? '' : 's'} in cart`);
      });

      document.querySelectorAll('.cart-bubble').forEach((bubble) => {
        if (bubble.closest(ROOT_SELECTOR)) return;

        const countElement = bubble.querySelector('.cart-bubble__text-count');
        if (countElement) {
          countElement.textContent = visibleValue;
          countElement.classList.toggle('hidden', numericCount === 0);
        }

        bubble.hidden = false;
        bubble.classList.toggle('visually-hidden', numericCount === 0);
        bubble.toggleAttribute('data-maintain-ratio', numericCount <= 99);

        const cartIcon = bubble.closest('cart-icon');
        cartIcon?.classList.toggle('header-actions__cart-icon--has-cart', numericCount > 0);
      });

      document.querySelectorAll('[data-testid="cart-count-live-region"]').forEach((region) => {
        region.textContent = `${value} item${numericCount === 1 ? '' : 's'} in cart`;
      });
    }
  }

  if (!customElements.get(ROOT_SELECTOR)) {
    customElements.define(ROOT_SELECTOR, MDSmartCart);
  }

  function bindGlobalEvents() {
    if (STATE.globalBound) return;
    STATE.globalBound = true;

    document.addEventListener('click', (event) => {
      const cart = activeCart();
      if (!cart) return;

      const explicitTrigger = event.target.closest('[data-md-smart-cart-open]');
      if (explicitTrigger) {
        event.preventDefault();
        event.stopImmediatePropagation();
        cart.open(explicitTrigger);
        return;
      }

      if (!toBool(cart.dataset.interceptCartLinks)) return;

      const link = event.target.closest('a[href]');
      if (!link || link.closest(ROOT_SELECTOR)) return;
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (link.target && link.target !== '_self') return;

      const headerContext = link.closest('header, header-component, #header-group, .shopify-section-group-header-group');
      if (!headerContext) return;

      let linkUrl;
      let cartUrl;
      try {
        linkUrl = new URL(link.href, window.location.origin);
        cartUrl = new URL(cart.dataset.cartUrl || `${cart.rootUrl}cart`, window.location.origin);
      } catch (error) {
        return;
      }

      if (linkUrl.origin === cartUrl.origin && linkUrl.pathname.replace(/\/$/, '') === cartUrl.pathname.replace(/\/$/, '')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        cart.open(link);
      }
    }, true);

    document.addEventListener('submit', async (event) => {
  const form = event.target;

  if (
    !(form instanceof HTMLFormElement) ||
    form.closest(ROOT_SELECTOR)
  ) {
    return;
  }

  /*
    Only intercept forms that have:
    data-md-smart-cart-add
  */
  if (!form.matches('[data-md-smart-cart-add]')) return;

  let action;

  try {
    action = new URL(
      form.action || window.location.href,
      window.location.origin
    );
  } catch (error) {
    return;
  }

  const actionPath = action.pathname
    .replace(/\/+/g, '/')
    .replace(/\/$/, '');

  if (
    !actionPath.endsWith('/cart/add') &&
    !actionPath.endsWith('/cart/add.js')
  ) {
    return;
  }

  const cart = activeCart();
  if (!cart) return;

  /*
    Critical:
    Prevent the standard form submission and /cart redirect.
  */
  event.preventDefault();
  event.stopImmediatePropagation();

  if (form.dataset.mdscSubmitting === 'true') return;

  form.dataset.mdscSubmitting = 'true';
  form.setAttribute('aria-busy', 'true');
  form.classList.add('is-loading');

  const submitter =
    event.submitter instanceof HTMLButtonElement ||
    event.submitter instanceof HTMLInputElement
      ? event.submitter
      : form.querySelector(
          'button[type="submit"], input[type="submit"], button[name="add"]'
        );

  const submitterWasDisabled = Boolean(submitter?.disabled);
  const originalButtonText =
    submitter instanceof HTMLButtonElement
      ? submitter.textContent
      : '';

  if (submitter) {
    submitter.disabled = true;

    if (submitter instanceof HTMLButtonElement) {
      submitter.textContent = cart.loadingText || 'Adding…';
    }
  }

  try {
    const formData = new FormData(form);

    /*
      Remove previously added section-rendering fields
      to avoid duplicate values.
    */
    formData.delete('sections');
    formData.delete('sections_url');

    formData.append('sections', cart.sectionId);
    formData.append('sections_url', window.location.pathname);

    const response = await window.fetch(
      `${cart.rootUrl}cart/add.js`,
      {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-MD-Smart-Cart': '1'
        },
        body: formData
      }
    );

    let data = null;

    try {
      data = await response.json();
    } catch (error) {
      if (!response.ok) {
        throw new Error(cart.errorText);
      }
    }

    if (!response.ok || data?.status >= 400) {
      throw new Error(
        data?.description ||
        data?.message ||
        cart.errorText
      );
    }

    /*
      Refresh and open the MIDUME Smart Cart.
    */
    await cart._applyCartResponse(data, true);
  } catch (error) {
    const currentCart = activeCart() || cart;
    const message =
      error.message ||
      currentCart.errorText ||
      'Unable to add this product.';

    currentCart._showGlobalError(message);
    currentCart._announce(message);
    currentCart.log(
      'Custom section add-to-cart failed',
      error
    );
  } finally {
    delete form.dataset.mdscSubmitting;
    form.removeAttribute('aria-busy');
    form.classList.remove('is-loading');

    if (submitter && document.contains(submitter)) {
      submitter.disabled = submitterWasDisabled;

      if (
        submitter instanceof HTMLButtonElement &&
        originalButtonText
      ) {
        submitter.textContent = originalButtonText;
      }
    }
  }
}, true);

    ['cart:update', 'cart:updated', 'cart:refresh', 'cart:change'].forEach((eventName) => {
      document.addEventListener(eventName, (event) => {
        const cart = activeCart();
        if (!cart || event.type.startsWith('md:')) return;

        const action = event.detail?.action || event.detail?.data?.action || '';
        const shouldOpen = action === 'add' || event.detail?.open === true;
        cart.scheduleSync(shouldOpen, 120);
      });
    });

    window.addEventListener('pageshow', (event) => {
      if (event.persisted && STATE.open) {
        activeCart()?.scheduleSync(true, 0);
      }
    });
  }

  function patchFetch() {
    if (STATE.fetchPatched || typeof window.fetch !== 'function') return;
    STATE.fetchPatched = true;

    const originalFetch = window.fetch.bind(window);

    window.fetch = async function mdSmartCartFetch(input, init) {
      const url = requestUrl(input);
      const marked = markerHeaderPresent(input, init);
      const response = await originalFetch(input, init);

      if (!marked && response.ok) {
        const isAdd = isCartEndpoint(url, 'add');
        const isChange = isCartEndpoint(url, 'change');
        const isUpdate = isCartEndpoint(url, 'update');

        if (isAdd || isChange || isUpdate) {
          window.setTimeout(() => {
            const cart = activeCart();
            if (!cart) return;
            cart.scheduleSync(isAdd || STATE.open, 30);
          }, 0);
        }
      }

      return response;
    };
  }

  bindGlobalEvents();
  patchFetch();

  document.addEventListener('shopify:section:load', () => {
    const cart = activeCart();
    if (cart && STATE.open) cart.open(STATE.lastTrigger, true);
  });
})();
